import {
  AbsoluteCenter,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import ContentBox from 'components/ContentBox';
import Card from 'components/card/Card';
import BackLink from 'components/links/BackLink';
import { Currency } from 'components/Number';
import { REVENUE_RECOGNITION_TYPE } from 'constants/dealConstants';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { downloadInvoicePdf } from './calc/downloadInvoicePdf';
import {
  deleteAccountingInvoiceApi,
  getAccountingInvoiceApi,
  markAccountingInvoicePaidApi,
} from 'api/accountingInternalApi';
import { getCustomerInstallmentsOverviewApi } from 'api/dealApi';
import ConfirmDialog from './components/ConfirmDialog';
import InvoicePreview from './components/InvoicePreview';
import PaymentStatusBadge from './components/PaymentStatusBadge';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isMarkPaidOpen,
    onOpen: onMarkPaidOpen,
    onClose: onMarkPaidClose,
  } = useDisclosure();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [repayments, setRepayments] = useState([]);
  const [loadingRepayments, setLoadingRepayments] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const stored = await getAccountingInvoiceApi({ invoiceId });
        if (!stored) {
          toast({
            title: 'Invoice not found',
            status: 'error',
          });
          navigate('/accounting-internal');
          return;
        }
        setInvoice(stored);
      } catch {
        toast({
          title: 'Invoice not found',
          status: 'error',
        });
        navigate('/accounting-internal');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invoiceId, navigate, toast]);

  // The invoice only stores repayment ids — their date/amount detail lives on
  // the customer's installments overview, so it's fetched separately once the
  // invoice (and its customerId) is known.
  useEffect(() => {
    const loadRepayments = async () => {
      if (!invoice?.customerId || !invoice.repaymentIds?.length) {
        setRepayments([]);
        return;
      }
      setLoadingRepayments(true);
      try {
        const overview = await getCustomerInstallmentsOverviewApi({
          customerId: invoice.customerId,
          revenueRecognition: REVENUE_RECOGNITION_TYPE.INTEREST_FIRST,
        });
        const linkedIds = new Set(invoice.repaymentIds);
        setRepayments((overview?.repayments || []).filter((r) => linkedIds.has(r.id)));
      } catch {
        // non-critical; the repayments list just stays empty
      } finally {
        setLoadingRepayments(false);
      }
    };
    loadRepayments();
  }, [invoice]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadInvoicePdf(invoice);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        status: 'error',
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    try {
      await markAccountingInvoicePaidApi({ invoiceId });
      // Re-read rather than patching locally: the backend owns amountPaid and
      // the resulting payment status.
      const refreshed = await getAccountingInvoiceApi({ invoiceId });
      setInvoice(refreshed);
      toast({
        title: 'Invoice marked as paid',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.error || 'Failed to mark this invoice as paid',
        status: 'error',
      });
    } finally {
      setMarkingPaid(false);
      onMarkPaidClose();
    }
  };

  /* Paired with the commented button above.

  const handleUnmarkPaid = async () => {
    setUnmarkingPaid(true);
    try {
      await unmarkAccountingInvoicePaidApi({ invoiceId });
      setInvoice(await getAccountingInvoiceApi({ invoiceId }));
      toast({ title: 'Invoice reverted to not paid', status: 'success' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to revert this invoice',
        status: 'error',
      });
    } finally {
      setUnmarkingPaid(false);
      onUnmarkPaidClose();
    }
  };

  */

  const handleDeleteInvoice = async () => {
    setDeletingInvoice(true);
    try {
      await deleteAccountingInvoiceApi({ invoiceId });
      toast({
        title: 'Invoice deleted',
        status: 'success',
      });
      navigate('/accounting-internal');
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete invoice',
        status: 'error',
      });
    } finally {
      setDeletingInvoice(false);
      onClose();
    }
  };

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  if (!invoice) {
    return null;
  }

  // An invoice with repayments linked to it (partially or fully paid) can't be deleted —
  // the delete button only appears once it's back to not-paid, e.g. after the linked
  // repayment(s) are deleted (see the Repayments section below).
  const canDeleteInvoice =
    invoice.paymentStatus !== 'partially_paid' && invoice.paymentStatus !== 'fully_paid';

  // An invoice's obligations are settled through the repayment screen; its
  // free-text charges never are. So Mark as paid closes a charges-only invoice
  // at any point, and a mixed one only once its obligations have been repaid —
  // which stops the repayment being bypassed. repayable is absent on invoices
  // created before charges existed, and every one of those has obligation
  // lines, so a missing value means repayable.
  const isRepayable = invoice.repayable !== false;
  const obligationsSettled = !isRepayable || (invoice.repaymentIds || []).length > 0;
  const canMarkPaid = obligationsSettled && invoice.paymentStatus !== 'fully_paid';

  return (
    <ContentBox>
      <BackLink text="Back to invoices" path="/accounting-internal" />
      <Card mt={6}>
        <Flex justify="space-between" align="center" mb={6} gap={4} wrap="wrap">
          <Box>
            <Flex align="center" gap={3} mb={1}>
              <Heading variant="sectionHeader" borderColor="twelve.green.300">
                {invoice.invoiceNumber}
              </Heading>
              {invoice.setOffEnabled ? (
                <Badge colorScheme="blue">Set-off attached</Badge>
              ) : null}
              <PaymentStatusBadge status={invoice.paymentStatus} />
            </Flex>
            <Text color="secondaryGray.600">{invoice.companyName}</Text>
            {invoice.createdByName ? (
              <Text fontSize="sm" color="secondaryGray.500">
                Created by {invoice.createdByName}
              </Text>
            ) : null}
            {invoice.paidManuallyAt ? (
              <Text fontSize="sm" color="secondaryGray.500">
                Marked paid by {invoice.paidManuallyByName || 'Unknown'} on{' '}
                {moment(invoice.paidManuallyAt).format('MMMM DD, YYYY')}
              </Text>
            ) : null}
            {invoice.receivingBankLabel ? (
              <Text fontSize="sm" color="secondaryGray.500">
                Receiving bank {invoice.receivingBankLabel}
              </Text>
            ) : null}
            {invoice.setOffEnabled && invoice.setOffCreatedDate ? (
              <Text fontSize="sm" color="secondaryGray.500">
                Set-off created {moment(invoice.setOffCreatedDate).format('MMMM DD, YYYY')}
              </Text>
            ) : null}
          </Box>
          <Flex gap={3} wrap="wrap">
            <Button variant="outline" onClick={() => navigate('/accounting-internal/new')}>
              Create another
            </Button>
            {canDeleteInvoice ? (
              <Button variant="outline" colorScheme="red" onClick={onOpen}>
                Delete invoice
              </Button>
            ) : null}
            {canMarkPaid ? (
              <Button variant="outline" colorScheme="green" onClick={onMarkPaidOpen}>
                Mark as paid
              </Button>
            ) : null}
            {/* Undoing a manual payment is supported by the API
                (POST /admin/accounting-invoices/:id/unmark-paid, which restores
                whatever the repayments and set-off actually cover). Not offered
                in the cockpit yet — to enable, uncomment this and the handler
                and dialog below, and import unmarkAccountingInvoicePaidApi.

            {invoice.paidManuallyAt ? (
              <Button variant="outline" onClick={onUnmarkPaidOpen}>
                Revert to not paid
              </Button>
            ) : null}
            */}
            <Button variant="brand" isLoading={downloadingPdf} onClick={handleDownloadPdf}>
              Download PDF
            </Button>
          </Flex>
        </Flex>
        <InvoicePreview
          invoiceNumber={invoice.invoiceNumber}
          invoiceDate={invoice.invoiceDate}
          dueDate={invoice.dueDate}
          billingDetails={invoice.billingDetails}
          lines={invoice.lines}
          setOffEnabled={invoice.setOffEnabled}
          requestReceivedDate={invoice.requestReceivedDate}
          fundingMonthKey={invoice.fundingMonthKey}
          growthInvestment={invoice.growthInvestment ?? invoice.purchaseAmount}
          growthInvestments={invoice.growthInvestments}
          setOffCreatedDate={invoice.setOffCreatedDate}
          signature={invoice.signature}
        />
      </Card>

      {(loadingRepayments || repayments.length > 0) && (
        <Card mt={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading variant="sectionHeader" borderColor="twelve.green.300">
              Repayments
            </Heading>
            <Text fontSize="sm" color="secondaryGray.600">
              {invoice.amountPaid ? (
                <>
                  <Currency value={invoice.amountPaid} maximumFractionDigits={2} /> of{' '}
                  <Currency value={invoice.totalDue} maximumFractionDigits={2} /> paid
                </>
              ) : null}
            </Text>
          </Flex>
          {loadingRepayments ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th isNumeric>Amount</Th>
                  <Th isNumeric>Set Off</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {repayments.map((repayment) => (
                  <Tr key={repayment.id}>
                    <Td>{repayment.paid_at ? moment(repayment.paid_at).format('MMM DD, YYYY') : '—'}</Td>
                    <Td isNumeric>
                      {/* repayment.amount (from the customer installments overview) only
                          reflects the installment allocation — any facility fee paid in the
                          same repayment isn't part of it. The invoice's own repaymentAmounts
                          map has the fee-inclusive total actually recorded against it. */}
                      <Currency
                        value={invoice.repaymentAmounts?.[repayment.id] ?? repayment.amount}
                        maximumFractionDigits={2}
                      />
                    </Td>
                    <Td isNumeric>
                      <Currency value={repayment.set_off_amount ?? 0} maximumFractionDigits={2} />
                    </Td>
                    <Td>
                      <Button
                        size="xs"
                        variant="link"
                        onClick={() => navigate(`/operations/${invoice.customerId}/installment`)}
                      >
                        View
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Card>
      )}

      {/* Paired with the commented button above.

      <ConfirmDialog
        isOpen={isUnmarkPaidOpen}
        title="Revert to not paid"
        message={`Revert invoice ${invoice.invoiceNumber} to not paid? It will go back to whatever its repayments and set-off cover.`}
        confirmLabel="Revert"
        onConfirm={handleUnmarkPaid}
        onClose={onUnmarkPaidClose}
        isLoading={unmarkingPaid}
      />

      */}

      <ConfirmDialog
        isOpen={isMarkPaidOpen}
        title="Mark invoice as paid"
        message={`You are now marking invoice ${invoice.invoiceNumber} as paid.`}
        confirmLabel="Mark as paid"
        onConfirm={handleMarkPaid}
        onClose={onMarkPaidClose}
        isLoading={markingPaid}
      />

      <ConfirmDialog
        isOpen={isOpen}
        title="Delete invoice"
        message={`Are you sure you want to delete invoice ${invoice.invoiceNumber}? This action cannot be undone.`}
        confirmLabel="Delete invoice"
        onConfirm={handleDeleteInvoice}
        onClose={onClose}
        isLoading={deletingInvoice}
      />
    </ContentBox>
  );
};

export default InvoiceDetail;
